import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Pencil, Trash2 } from "lucide-react";
import {
  useUpdateMoverMutation,
  useGetDocumentSignedUrlMutation,
} from "@/store/mover/apis";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { Document } from "@/types";

const Documents: React.FC = () => {
  const dispatch = useDispatch();
  const mover = useSelector(getSelectedMover);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(
    null
  );
  const [documents, setDocuments] = useState<Document[]>([]);
  const { control, handleSubmit, reset } = useForm<Document>();
  const [updateMover] = useUpdateMoverMutation();
  const [getSignedUrl] = useGetDocumentSignedUrlMutation();
  const [file, setFile] = useState<File | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (mover) {
      setDocuments(mover.documents);
    }
  }, [mover]);

  const handleEdit = (document: Document) => {
    setEditingDocument(document);
    reset(document);
    setIsModalOpen(true);
    setFile(null);
  };

  const handleDelete = (id: number) => {
    setDeletingDocumentId(id);
    setIsDeleteModalOpen(true);
  };

  const onSubmit = async (data: Document) => {
    try {
      const documentData = { ...data };

      if (documentData.id === null && !file) {
        throw new Error("No file selected");
      }

      let payload = {
        ...documentData,
      };

      if (file) {
        const signedUrlResponse = await getSignedUrl({
          filename: file.name,
          content_type: file.type,
        }).unwrap();

        if (!signedUrlResponse.data) {
          throw new Error("Failed to get signed URL");
        }

        const { signed_url, key } = signedUrlResponse.data;

        const response = await fetch(signed_url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
          mode: "cors",
        });

        if (!response.ok) {
          throw new Error("Failed to get signed URL");
        }

        payload = {
          ...payload,
          file_name: file.name,
          file_path: key,
          file_content_type: file.type,
          file_size: file.size,
        };
      }

      const updatedMover = await updateMover({
        data: {
          documents_attributes: [payload],
        },
      }).unwrap();

      if (!updatedMover.error) {
        dispatch(updateSelectedMover(updatedMover));
      }

      toast({
        title: "Success",
        description: "Document updated successfully",
      });
      setIsModalOpen(false);
      refreshDocuments();
    } catch (error) {
      // TODO: send error to sentry
      console.error("error", error);
      toast({
        title: "Error",
        description: "Failed to update document",
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const updatedMover = await updateMover({
        data: {
          documents_attributes: [{ id: deletingDocumentId, _destroy: true }],
        },
      }).unwrap();

      if (!updatedMover.error) {
        dispatch(updateSelectedMover(updatedMover));
      }

      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
      setIsDeleteModalOpen(false);
      refreshDocuments();
    } catch (error) {
      // TODO: send error to sentry
      console.error("error", error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const refreshDocuments = async () => {
    setDocuments(mover?.documents || []);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between">
        <h2 className="text-lg font-bold">Settings &gt; Documents</h2>
        <Button
          onClick={() => {
            setEditingDocument(null);
            reset({
              name: "",
              send_with_estimates: false,
              send_with_confirmations: false,
            });
            setIsModalOpen(true);
            setFile(null);
          }}
        >
          Add Document
        </Button>
      </div>

      <Table className="mt-4">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Send with Estimates</TableHead>
            <TableHead>Send with Confirmations</TableHead>
            <TableHead>Document</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => (
            <TableRow key={doc.id}>
              <TableCell>{doc.name}</TableCell>
              <TableCell>{doc.send_with_estimates ? "YES" : "NO"}</TableCell>
              <TableCell>
                {doc.send_with_confirmations ? "YES" : "NO"}
              </TableCell>
              <TableCell>
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline"
                >
                  {doc.file_name}
                </a>
              </TableCell>
              <TableCell className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEdit(doc)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleDelete(doc.id!)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDocument ? "Edit Document" : "Add Document"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Document name is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <div className="col-span-3">
                      <Input id="name" {...field} />
                      {error && (
                        <p className="text-sm text-red-500">{error.message}</p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Send with Estimates</Label>
                <Controller
                  name="send_with_estimates"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Send with Confirmations</Label>
                <Controller
                  name="send_with_confirmations"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="file" className="text-right">
                  Upload File
                </Label>
                {file ? (
                  <p>{file.name}</p>
                ) : (
                  editingDocument?.file_url && (
                    <a
                      href={editingDocument.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {editingDocument.file_name}
                    </a>
                  )
                )}
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogTrigger asChild>
              <Button type="submit">
                {editingDocument ? "Save changes" : "Add Document"}
              </Button>
            </DialogTrigger>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this document?</p>
          <DialogTrigger asChild>
            <Button onClick={handleDeleteConfirm}>Delete</Button>
          </DialogTrigger>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
